/**
 * authFetch — fetch wrapper that survives a transient 401 by kicking off
 * a token refresh and retrying once.
 *
 * Why this exists: the canonical `api/client.ts::request()` helper has
 * the 401-retry logic inline, but several modules (fieldMap, edges,
 * usePostBlocks) ship their own thin fetch wrappers because they handle
 * non-JSON responses, errors, or query-strings differently. Those
 * wrappers all share the same trap: a polling loop hits 401 the moment
 * the access-token expires, and even though the refresh-token is still
 * valid, the loop never recovers — the console fills with permanent
 * 401s and the UI degrades silently.
 *
 * Use this when you'd otherwise be writing
 *   const headers = { Authorization: `Bearer ${getAccessToken()}` }
 *   const res = await fetch(url, { headers })
 *
 * Pass the URL + an optional RequestInit. The Authorization header is
 * injected when a token exists, on every attempt (so the second try
 * picks up the freshly-refreshed token). Returns the final Response —
 * caller decides whether to .json()/.text()/.blob() it.
 */

import { getAccessToken, refreshAccessToken } from '../auth'

export interface AuthFetchInit extends RequestInit {
  /** Defaults to true. Pass false to skip the refresh-on-401 retry. */
  retryOnUnauthorized?: boolean
}

export async function authFetch(
  url: string,
  init: AuthFetchInit = {},
): Promise<Response> {
  const { retryOnUnauthorized = true, headers: callerHeaders, ...rest } = init

  const buildHeaders = (): HeadersInit => {
    const h: Record<string, string> = {}
    if (callerHeaders) {
      // Preserve caller-supplied headers (e.g. Content-Type) without
      // overwriting them with the Authorization we add below.
      Object.assign(h, callerHeaders as Record<string, string>)
    }
    const token = getAccessToken()
    if (token) h.Authorization = `Bearer ${token}`
    return h
  }

  let res = await fetch(url, { ...rest, headers: buildHeaders() })

  if (res.status === 401 && retryOnUnauthorized) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      res = await fetch(url, { ...rest, headers: buildHeaders() })
    }
  }

  return res
}
